// Angular imports
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy, ViewChildren, QueryList } from '@angular/core';

// Import rxjs
import { BehaviorSubject, Subject, distinctUntilChanged, takeUntil } from 'rxjs';

// Import modals
import { ModalService } from '../../modal';
import { LineCommentModalComponent } from '../../modals';

// Import types
import { CalculationLineApi, CalculationPercentagesApi, CalculationMetaApi, CalculationTailApi, HasValueSets } from '../../types';

// Import column and selection types
import { ColumnMap } from '../../types';
import { Rect } from '../types';

// Import types
import { LineComment } from '../../types';
import { LineTag } from '../cell';

// Import components
import { CellComponent } from '../cell/cell.component';

// Import pipes
import { LineTagsPipe } from '../../pipes';

// Import services
import { UndoService } from '../../services/undo.service';
import { Aggregation, CalculationSelection, TailLineType, ViewModelLineType, ViewModelLine, WarningType, isCalculationLineApi, isCalculationTailApi } from 'src/app/state-management/calculation-state';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state-management/app-state';
import * as CalculationActions from 'src/app/state-management/calculation-actions';
import { selectCalculationMeta } from 'src/app/state-management/calculation-selectors';
import { StaleProperty } from 'src/app/state-management/undo-redo';

@Component({
    selector: 'line-row',
    templateUrl: './line-row.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./line-row.component.scss'],
    providers: [LineTagsPipe],
    standalone: false
})
export class LineRowComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    public viewLine: ViewModelLine;
    public line: CalculationLineApi | CalculationTailApi;
    public aggregation: Aggregation;
    public warnings: WarningType[] = [];

    @Input() public set lineValue(value: ViewModelLine) {
        this.viewLine = value;
        this.line = JSON.parse(JSON.stringify(value.line));

        if (isCalculationLineApi(value.line)) {
            this.warnings = value.warnings ?? [];
            this._lineTags = this.lineTagsPipe.transform(value.line);
        } else if (isCalculationTailApi(value.line)) {
        }
        // console.log('SET', value);

        this.aggregation = value.aggregation;
        // this._staleProperties$ = value.staleProperties$;
    }
    @Input() public percentages: CalculationPercentagesApi;
    @Input() public index: number;
    @Input() public columns: ColumnMap;
    @Input() public allowedUnits: string[] = [];

    @Output() public change = new EventEmitter<void>();

    // Selection
    @Input() public selection: CalculationSelection;
    @Output() public selectionChange = new EventEmitter<Selection>();

    // Events for moving selection
    @Output() public selectAbove = new EventEmitter<void>();
    @Output() public selectBelow = new EventEmitter<void>();
    @Output() public selectLeft = new EventEmitter<void>();
    @Output() public selectRight = new EventEmitter<void>();

    // Event for recalulating the height of the line
    @Output() public recalculateHeight = new EventEmitter<void>();

    // Event for updating selection highlight
    @Output() public updateSelectionHighlight = new EventEmitter<void>();

    // List of all cells
    @ViewChildren('cell') public _cellViews: QueryList<CellComponent<any>>;

    public _types: TailLineType[] = [
        '€', '%', 't'
    ];

    // public _calculation: Calculation;
    public meta: CalculationMetaApi;
    public _lineTags: LineTag[] = [];
    public _staleProperties$: BehaviorSubject<StaleProperty[]>;

    constructor(private undoService: UndoService,
        private lineTagsPipe: LineTagsPipe,
        private modalService: ModalService,
        private store: Store<AppState>
    ) {
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$),
            distinctUntilChanged()
        ).subscribe(meta => {
            this.meta = meta;
        });
    }

    public _getType(line: ViewModelLine): TailLineType {
        return line.type === ViewModelLineType.TAIL_AMOUNT ? '€' : line.type === ViewModelLineType.TAIL_PERCENTAGE ? '%' : 't';
    }


    public _HandleChangeType(type: TailLineType) {
        // const oldType = this.viewLine.type === ViewModelLineType.TAIL_AMOUNT ? 'AMOUNT' : this.viewLine.type === ViewModelLineType.TAIL_PERCENTAGE ? 'PERCENTAGE' : 'TEXT';
        const newType = type === '€' ? 'AMOUNT' : type === '%' ? 'PERCENTAGE' : 'TEXT';

        // Create a copy of the line to send
        const updatedLine: CalculationTailApi = { ...this.line };
        updatedLine.type = newType;
        this.store.dispatch(CalculationActions.updateTailLineUi({ projectId: this.meta.projectId, calculationId: this.meta.id, line: updatedLine }));
    }

    public ngOnInit() {
    }

    public ngOnDestroy() {
        // Unsubscribe
        // this.subscriptions.forEach(sub => sub.unsubscribe());
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /* --- Getters & setters --- */

    public get selectionCurrentCellRect(): Rect | undefined {
        if (!this.selection || this.selection.current.row !== this.index) return undefined;

        // Get cell rect
        return this.getCellRect(this.selection.current.column);
    }

    public get selectionOriginCellRect(): Rect | undefined {
        if (!this.selection?.origin || this.selection.origin!.row !== this.index) return undefined;

        // Return cell rect
        return this.getCellRect(this.selection.origin!.column);
    }

    /* --- Functions --- */

    public focusCell(columnIndex: number, shallow: boolean = true): void {
        // Get the cell view for the column
        const cell = this.getCell(columnIndex);

        // If a valid cell was found, focus the cell
        if (cell)
            cell.focus(shallow);
    }

    public blurCell(columnIndex: number): void {
        // Get the cell view for the column
        const cell = this.getCell(columnIndex);

        // If a valid cell was found, blur the cell
        if (cell)
            cell.blur();
    }

    public selectCell(columnIndex: number): void {
        // Get the cell view for the column
        const cell = this.getCell(columnIndex);

        // If a valid cell was found, select the cell
        if (cell)
            cell.select(false);
    }

    public getCellRect(columnIndex: number): Rect | undefined;
    public getCellRect(column: string): Rect | undefined;
    public getCellRect(column: string | number): Rect | undefined {
        // Get column index
        const columnIndex = typeof (column) === 'number' ? column : this.columns[column].index;

        // Get the cell
        const cell = this._cellViews.find(cell => cell.column.index === columnIndex);
        if (!cell) return undefined;

        // Return bounding rect
        return cell.getRelativeBoundingRect();
    }

    /* --- Transforms --- */

    /* --- Event handlers --- */

    public _setPropertyUndo(property: string, newValue: any) {
        // const oldValue = this.line[property];
        this.line[property] = newValue;

        // Emit change event
        this._handleCellChange();
    }

    public _setValueSetUndo(set: HasValueSets, property: 'norm' | 'percent', newValue: number) {
        const oldValue = set[property] ?? 0; // Must default to 0 to erase newValue
        set[property] = newValue;

        // Emit change event
        this._handleCellChange();
    }

    public _handleSelectionChange(selection: CalculationSelection) {
        this.selection = {
            ...selection,
            current: {
                row: this.index, // Add row here
                column: selection.current.column
            },
            selectedLines: [JSON.parse(JSON.stringify(this.viewLine))]
        };
        this.store.dispatch(CalculationActions.updateSelection({
            selection: this.selection,
        }));
    }

    public _handleShowCommentModal() {
        if (!isCalculationLineApi(this.line)) {
            return;
        }

        if (this.line.isTextLine) return;

        // Show comment modal
        this.modalService.open(LineCommentModalComponent, {
            comment: this.line.comment,
            done: (comment: LineComment) => {
                // console.log('set comment', comment);

                // Set comment
                (this.line as CalculationLineApi).comment = comment;

                // Handle cell change
                this._handleCellChange();
            }
        });
    }

    public _recalulateLineHeight() {
        // this.change.emit();
        // this.recalculateHeight.emit();
    }

    public _updateSelectionHighlight() {
        this.updateSelectionHighlight.emit();
    }

    public _handleSelectAbove() {
        this.selectAbove.emit();
    }

    public _handleSelectBelow() {
        this.selectBelow.emit();
    }

    public _handleSelectLeft() {
        this.selectLeft.emit();
    }

    public _handleSelectRight() {
        this.selectRight.emit();
    }

    public _handleCellChange() {
        // // console.log((this.line as CalculationLine).quantity);

        // // Check for warnings
        // this._hasWarnings = this.line.warnings.length > 0;

        // // Set line tags
        if (isCalculationLineApi(this.line)) {
            this._lineTags = this.lineTagsPipe.transform(this.line);

            // // Emit change events
            this.store.dispatch(CalculationActions.updateLineUi({
                projectId: this.meta.projectId,
                calculationId: this.meta.id,
                line: JSON.parse(JSON.stringify(this.line))
            }))
        } else if (isCalculationTailApi(this.line)) {
            // // Emit change events
            this.store.dispatch(CalculationActions.updateTailLineUi({
                projectId: this.meta.projectId,
                calculationId: this.meta.id,
                line: JSON.parse(JSON.stringify(this.line))
            }))
        }
    }

    /* --- Internal --- */

    private getCell(columnIndex: number): CellComponent<any> | undefined {
        return this._cellViews.find(cell => {
            return cell.column ? cell.column.index === columnIndex : false;
        });
    }
}
